<template>
  <div 
    class="fts-calendar" 
    :data-calendar-load="loading ? 'loading' : 'loaded'" 
    @scroll="onScroll"
  >
    <div class="fts-client-header" ref="clientHeader">
      <div class="client-header-wrapper">
        <div class="client-header__l">
          <div class="client-header-label">
            <font-awesome-icon :icon="['far', 'calendar']" size="lg" class="client-header-label-icon" />
            <div class="client-header-label-title">
              My Calendar
            </div>
          </div>
        </div>
        <div class="client-header__r">
          <!-- refresh -->
          <fts-tippy
            content="Refresh"
            class="client-header-action"
          >
            <fts-btn
              slot="trigger"
              icon
              text
              :loading="loading"
              @click="getCalendarEvents()"
            >
              <font-awesome-icon :icon="['fas', 'sync-alt']" />
            </fts-btn>
          </fts-tippy>
        </div>
      </div>
    </div>

    <div class="selection-container">
      <div class="selection-outer">
        <div
          v-for="selection in selections.filter((x) => !x.deleted)"
          :key="selection.id"
          class="selection-wrapper"
          :class="{ disabled: !selection.show }"
          :style="{ borderColor: selection.color }"
          @click="toggleSelection(selection)"
        >
          <div class="selection-info" v-if="selection.task">
            <div class="selection-info-name">My Task Dues</div>
          </div>

          <template v-else>
            <fts-tippy
              content="Calendar accessible to all"
              class="selection-access"
              v-if="selection.everyone"
            >
              <font-awesome-icon :icon="['fas', 'user-friends']" />
            </fts-tippy>

            <fts-label-group
              :item="{
                'name': selection.listName,
                'emoji': selection.listEmoji
              }"
              emoji
              :emoji-size="14"
              class="selection-listing"
              v-if="selection.listId"
            />

            <fts-label-group
              :item="{
                'name': selection.moduleName,
                'emoji': selection.moduleEmoji
              }"
              emoji
              :emoji-size="14"
              class="selection-module"
            />

            <swatches
              row-length="5"
              :swatches="colorSchemes"
              shapes="circles"
              popover-x="right"
              popover-y="top"
              :swatch-size="25"
              :trigger-style="{ width: '14px', height: '14px' }"
              v-model="selection.color"
              class="selection-picker stopPropagation"
              @input="getCalendarEvents"
            />

            <font-awesome-icon
              :icon="['fas', 'trash']"
              class="selection-remove stopPropagation"
              @click="removeSelection(selection)"
              v-if="!selection.everyone || isAdminUser"
            />
          </template>
        </div>
      </div>
    </div>
    <!-- full calendar -->
    <fts-full-calendar
      :events="events"
      full-height
      @prev="getCalendarEvents"
      @next="getCalendarEvents"
      @today="getCalendarEvents"
      @event:click="eventClick"
      class="calendar-body"
      ref="ftsFullCalendar"
    >
      <template #eventContent="{ event, isStart, isEnd }">
        <fts-tippy
          v-if="isFullDayEvent(event)"
          :content="getEventTitle(event, isStart, isEnd)"
          trigger-class="fc-event-content-title"
          class="fc-event-content"
        >
          {{ getEventTitle(event, isStart, isEnd) }}
        </fts-tippy>
        <fts-tippy
          v-else
          :content="getEventTitle(event, isStart, isEnd)"
        >
          <div
            class="fc-event-content-with-time"
            :style="{
              'color': 'var(--fts-text)',
              'border-color': event.backgroundColor
            }"
          >
            <div>{{ getEventTitle(event, isStart, isEnd) }}</div>
          </div>
        </fts-tippy>
      </template>
    </fts-full-calendar>
  </div>
</template>

<script>
import moment from 'moment';
import Swatches from 'vue-swatches';

import commons from '../commons/commons';
import { darkPalette } from '../commons/colors';
import { clientStore } from '../store/clientStore';
import { authorizationStore } from '../store/authorizationStore';

import FTSBtn from '../elements/FTSBtn';
import FTSFullCalendar from '../elements/FTSFullCalendar';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSMoji from '../elements/FTSMoji';
import FTSTippy from '../elements/FTSTippy';
import FTSToggleSwitch from '../elements/FTSToggleSwitch';

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-full-calendar": FTSFullCalendar,
    "fts-label-group": FTSLabelGroup,
    "fts-moji": FTSMoji,
    "fts-tippy": FTSTippy,
    "fts-toggle-switch": FTSToggleSwitch,
    Swatches,
  },
  data: () => ({
    events: [],
    selections: [],
    colorSchemes: darkPalette,
    loading: false
  }),
  methods: {
    moment,
    onScroll(evt) {
      try {
        const isScroll = evt.target.scrollTop > 0;
        if (isScroll)
          this.$refs.clientHeader.classList.add('fts-client-header--scroll');
        else if (!isScroll)
          this.$refs.clientHeader.classList.remove('fts-client-header--scroll');
      } catch(err) {
        console.warn("[DOMException]", err);
      }
    },
    eventClick(item) {
      window.eventBus.$emit("quickViewData", {
        id: item && item.payload && item.payload.id,
        vectorId: item && item.payload && item.payload.vectorId,
      });
    },
    toggleSelection(selection) {
      if (commons.elementStopPropagation()) return;
      this.$set(selection, "show", !selection.show);
      this.getCalendarEvents();
    },
    removeSelection(selection) {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete Calendar?`,
        buttons: [
          { title: "Cancel"},
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$set(selection, "deleted", true);
              this.getCalendarEvents();
            }
          }
        ]
      });
    },
    async getCalendarEvents() {
      this.loading = true;

      let payload = {
        startDate: moment(
          this.$refs.ftsFullCalendar.fullCalendarApi.view.activeStart
        ).format("YYYY-MM-DD"),
        endDate: moment(
          this.$refs.ftsFullCalendar.fullCalendarApi.view.activeEnd
        ).format("YYYY-MM-DD"),
        workspaceOnly: null,
      };

      if (this.selections)
        payload = {
          ...payload,
          selections: this.selections,
        };

      try {
        let res = await clientStore.dispatch("getMyCalender", payload);
        this.selections = res.data.selections.map((x) => {
          let selection = x;
          const findModule = this.workspaceModules.find((y) => y.id == x.vectorId);
          if (findModule) {
            selection.moduleName = findModule.name;
            selection.moduleEmoji = findModule.emoji;
            if (x.listId) {
              const findList = findModule.menus.find((y) => y.id === x.listId);
              if (findList) {
                selection.listName = findList.name;
                selection.listEmoji = findList.emoji;
              }
            }
          }
          return selection;
        });

        this.events = res.data.calendarEvents.map((x) => {
          let event = {
            id: commons.nanoID(),
            entryId: x.id,
            title: `${x.title}`,
            start: x.start,
            payload: x
          };
          const findCalendar = this.selections.find((y) => y.id == x.calendarId);
          if (findCalendar) {
            event.order = this.selections.indexOf(findCalendar);
            Boolean(findCalendar.color) && (event.color = findCalendar.color);
          } else {
            event.order = 0;
          }
          Boolean(x.end) && (event.end = x.end);
          return event;
        }).sort((a, b) => {
          if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;

          const dateA = new Date(a.start);
          const dateB = new Date(b.start);
          const timeA = dateA.getHours() * 3600 + dateA.getMinutes() * 60 + dateA.getSeconds();
          const timeB = dateB.getHours() * 3600 + dateB.getMinutes() * 60 + dateB.getSeconds();
          return timeA - timeB;
        });
      } catch (err) {
        console.error(err);
      }

      this.loading = false;
    },
    getEventTitle(event, isStart, isEnd){
      const payload = event && event.extendedProps && event.extendedProps.payload;

      if (!payload || !payload.title) return '(untitled)';

      const { title, start, end, allDay } = payload;
      const timeFormat = 'h:mm a';

      if (allDay === true) return title;

      function formatTime(date) {
        return date ? moment(date).format(timeFormat) : null;
      }

      let startStr, endStr;

      if (isStart && !isEnd) {
        startStr = formatTime(start);
        endStr = this.formatLocalDateTime(end);
      } else if (!isStart && isEnd) {
        startStr = this.formatLocalDateTime(start);
        endStr = formatTime(end);
      } else if (isStart && isEnd) {
        startStr = formatTime(start);
        endStr = formatTime(end);
      } else {
        startStr = this.formatLocalDateTime(start);
        endStr = this.formatLocalDateTime(end);
      }
      if (endStr) {
        return `${startStr} - ${endStr} ${title}`;
      } else {
        return `${startStr} ${title}`;
      }
    },
    isFullDayEvent(event){
      const eventPayload = event && event.extendedProps && event.extendedProps.payload;
      return eventPayload.allDay && eventPayload.allDay === true;
    },
    formatLocalDateTime(date = new Date()){
      const datePart = moment(date).format('MMM D, YYYY');
      const timePart = moment(date).format('h:mm a');

      return `${datePart} ${timePart}`;
    }
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    workspaceModules() {
      return clientStore.state.workspaceModules;
    }
  },
  async mounted() {
    this.$nextTick(async () => {
      await this.getCalendarEvents();

      if (this.$route.query.id) {
        const queryDecodeids = commons.queryDecodeids(this.$route.query.id);
        if (queryDecodeids.moduleId && queryDecodeids.entryId) {
          window.eventBus.$emit("quickViewData", {
            id: queryDecodeids.entryId,
            vectorId: queryDecodeids.moduleId,
            activityId: this.$route.query.a,
          });
        }
      }
    });
    window.eventBus.$on("calendarRefresh", () => {
      this.getCalendarEvents();
    });
  },
  beforeDestroy() {
    window.eventBus.$off("calendarRefresh")
  }
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-calendar {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  .calendar-body {
    flex: 1;
    padding: 1rem;
  }
  .selection-container {
    margin-top: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    .selection-outer {
      @include flex-gap($gap: 0.5rem);
      .selection-wrapper {
        position: relative;
        min-width: 0;
        display: flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
        border: 1px solid var(--fts-gray-200);
        border-radius: 0.25rem;
        font-size: 0.875rem;
        color: var(--fts-text);
        cursor: pointer;
        &.disabled {
          .selection-info {
            filter: grayscale(75%);
          }
        }
        .selection-info {
          min-width: 0;
          display: flex;
          align-items: center;
        }
        .selection-info-name {
          @include text-overflow-hidden;
        }
        .selection-listing {
          & + .selection-module {
            margin-left: 0.5rem
          }
        }
        .selection-access {
          margin-right: 0.75rem;
          color: var(--fts-default);
          font-size: 0.75rem;
        }
        .selection-picker {
          margin-left: 0.75rem;
          display: inline-flex;
        }
        .selection-remove {
          margin-left: 0.75rem;
          font-size: 0.75rem;
          color: var(--fts-gray-400);
          cursor: pointer;
          &:hover {
            color: var(--fts-danger);
          }
        }
      }
    }
  }
  .fc-event-content-with-time {
    height: 22px;
    display: flex;
    align-items: center;
    margin: -1px;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border: 1px solid var(--fts-border);
    border-radius: 3px;
    background-color: var(--fts-background);
    font-size: 0.75rem;
    line-height: 1.5;
    cursor: pointer;
    > div {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .fc-direction-ltr .fc-daygrid-block-event:not(.fc-event-end), 
  .fc-direction-rtl .fc-daygrid-block-event:not(.fc-event-start) {
    .fc-event-content-with-time { 
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right-width: 0;
    }
  }
  .fc-direction-ltr .fc-daygrid-block-event:not(.fc-event-start), 
  .fc-direction-rtl .fc-daygrid-block-event:not(.fc-event-end) {
    .fc-event-content-with-time {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left-width: 0;
    }
  }
}
</style>

<style lang="scss">
/* --- Calm density, style-only round (docs/frontend-calendar-calm-density-proposal.md,
   rulings C0-C13). CSS only: no template, prop, copy or data is touched. This file's own
   block is scoped and FullCalendar's DOM carries no scope id, so the round lands here, in
   a second unscoped block namespaced on the Client shell root intersected with this page's
   root. That leaves the module listing's calendar view (.fts-calendar-view, the other
   consumer of the same frame), every other FullCalendar instance, and the header shared
   with Dashboard and Lobby exactly as they are. No !important. The header, the ghost icon button and the --cd-* tokens are shared with
   Dashboard and Lobby and live in Client.vue; this block is what belongs to the calendar alone. */
.fts-client.fts-client--refined .fts-calendar {
  /* C2, C3, C4 - the calendar row: 32px chips carrying two controls at rest become 26px
     chips whose colour is a left rule, whose controls arrive on hover or focus, and whose
     off state is finally legible. Until now .disabled only greyed .selection-info, which
     the module chips never render, so a calendar switched off looked switched on. */
  .selection-container {
    .selection-wrapper {
      padding: 0.125rem 0.5rem;
      border-width: 1px 1px 1px 3px;
      border-top-color: var(--cd-hairline);
      border-right-color: var(--cd-hairline);
      border-bottom-color: var(--cd-hairline);
      border-radius: 0.25rem;
      font-size: 0.75rem;
      line-height: 1.375rem;
      &:hover {
        background-color: var(--cd-hover);
      }
      &.disabled {
        opacity: 0.55;
        border-left-style: dashed;
        color: var(--cd-ink-2);
      }
      .selection-access {
        margin-right: 0.5rem;
        font-size: 0.6875rem;
      }
      .selection-listing + .selection-module {
        margin-left: 0.375rem;
      }
      .selection-picker,
      .selection-remove {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.12s ease;
      }
      .selection-picker {
        margin-left: 0.5rem;
      }
      .selection-remove {
        margin-left: 0.5rem;
        font-size: 0.6875rem;
      }
      &:hover,
      &:focus-within {
        .selection-picker,
        .selection-remove {
          opacity: 1;
          pointer-events: auto;
        }
      }
    }
  }

  /* C5 - the month header: 18/500 over three outlined buttons becomes 15/600 over ghosts,
     with Today on a neutral ground because it is the one that gets pressed */
  .fts-full-calendar {
    .fc-header {
      margin-bottom: 0.5rem;
      .fc-header-title {
        font-weight: 600;
        font-size: 0.9375rem;
        color: var(--fts-text);
      }
      .fc-header-r {
        display: flex;
        gap: 0.125rem;
        .fts-btn.fts-btn-v2.fts-btn-outline {
          min-height: 1.5rem;
          padding-left: 0.5rem;
          padding-right: 0.5rem;
          border: 0;
          border-radius: 0.25rem;
          background-color: transparent;
          color: var(--cd-ink-2);
          font-size: 0.75rem;
          letter-spacing: 0;
          &:hover {
            background-color: var(--cd-hover);
            color: var(--fts-text);
          }
          &:last-child {
            background-color: var(--fts-gray-100);
            color: var(--fts-text);
            font-weight: 500;
          }
        }
      }
    }
  }

  /* C8 - the grid gets two weights: a hairline inside, the frame a shade stronger */
  .fc {
    .fc-view-harness {
      .fc-scrollgrid {
        border-color: var(--cd-hairline);
        tr > th,
        tr > td {
          border-color: var(--cd-rule);
        }
      }
      .fc-col-header .fc-col-header-cell .fc-col-header-cell-cushion {
        padding-top: 0.375rem;
        padding-bottom: 0.375rem;
        font-weight: 500;
        font-size: 0.6875rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--cd-ink-2);
      }

      /* C7 - 42px of number per cell becomes 32px, which is a whole event row back in
         every cell of the month. Today keeps the primary circle: with the fills washed it
         is the only accent left on the page. */
      .fc-daygrid-day {
        .fc-daygrid-day-top {
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
          > a {
            height: 24px;
            width: 24px;
            font-size: 0.6875rem;
            color: var(--cd-ink-2);
          }
        }
        /* SS06271: the grey ink above outranks FTSFullCalendar's white on today's primary
           circle (more classes), which left the number at about 1.2:1. Restate it here. */
        &.fc-day-today .fc-daygrid-day-top > a {
          font-weight: 500;
          color: #fff;
        }
      }

      /* C9 - the popover is the only real overlay left, so it keeps one elevation */
      .fc-popover {
        border-color: var(--cd-hairline);
        border-radius: 0.375rem;
        box-shadow: var(--fts-elevation-2);
        font-size: 0.8125rem;
        .fc-popover-header {
          padding: 0.5rem 0.75rem;
          border-bottom-color: var(--cd-rule);
          font-weight: 600;
          font-size: 0.75rem;
        }
      }
    }
    .fc-daygrid-day-bottom {
      font-size: 0.75rem;
      color: var(--cd-ink-2);
      .fc-more-link:hover {
        color: var(--fts-text);
      }
    }
  }

  /* C6, C10 - an all-day event is a solid block of the calendar's colour with white text;
     the timed event beside it is a white pill with that colour as a border. The wash moves
     the first into the second's family: background-image paints over the inline fill, the
     inline border-color survives as a 3px mark because CSS owns the border's width, and
     the ink is ours to choose (FullCalendar sets .fc-event-main's colour inline only when
     a textColor is configured, and these calendars never set one). */
  .fts-full-calendar .fc .fc-daygrid-event.fc-daygrid-block-event {
    background-image: linear-gradient(var(--cd-wash), var(--cd-wash));
    border-width: 0 0 0 3px;
    border-radius: 0.25rem;
    &:hover {
      --cd-wash: rgba(252, 252, 252, 0.78);
    }
    .fc-event-content {
      height: 20px;
      color: var(--fts-gray-700);
    }
    /* a segment that is not the start of its run carries no mark */
    &:not(.fc-event-start) {
      border-left-width: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    &:not(.fc-event-end) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  /* the timed pill only loses a couple of pixels: its border colour is written inline by
     this page's own template, so the hue stays on all four sides */
  .fc-event-content-with-time {
    height: 20px;
    border-radius: 0.25rem;
  }
}
</style>
